import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { UISelect } from "../../ui";
import messages from "../../../assets/locale/messages";
import { getClientPortalCompanies } from "../../../network/apis/clientPortalCompanies";
import { loginResponse } from "../../../store/Auth/actions";
import { setAdmin } from "../../../utils/Auth";
import { clx } from "../../../utils/Helpers";
import {
  applyClientCompanySelection,
  clientHasMultipleCompanies,
  getClientCompanies,
  getClientPortalLogin,
  isSameClientCompanyId,
  mergeClientCompaniesIntoLogin,
  normalizeClientCompanyOption,
  resolveClientSelectedCompanyOption,
} from "../../../utils/clientPortal";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";

import "./ClientCompanySwitcher.scss";

const ClientCompanySwitcher = ({ className }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const { clientCompanySelection: copy, shared } = messages[lang];

  const loginCompanies = login?.companies;
  const loginSelectedCompany = login?.selected_company;

  const companyOptions = useMemo(
    () => getClientCompanies(login).map(normalizeClientCompanyOption),
    [loginCompanies],
  );
  const hasMultiple = clientHasMultipleCompanies(login);

  useEffect(() => {
    if (!hasMultiple) return;

    let cancelled = false;

    getClientPortalCompanies({ page: -1 })
      .then((companiesResponse) => {
        if (cancelled) return;
        const companyRows = companiesResponse?.data?.data;
        if (!Array.isArray(companyRows) || companyRows.length === 0) return;

        const nextLogin = mergeClientCompaniesIntoLogin(
          getClientPortalLogin(),
          companyRows,
        );
        dispatch(loginResponse(nextLogin));
        setAdmin(nextLogin);
      })
      .catch(() => {
        /* keep companies from login payload */
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, hasMultiple, lang]);

  const selectedCompany = useMemo(() => {
    const resolved = resolveClientSelectedCompanyOption(login);
    if (!resolved?.id) return null;

    const match = companyOptions.find((company) =>
      isSameClientCompanyId(company.id, resolved.id),
    );
    if (match) return match;

    return normalizeClientCompanyOption(resolved);
  }, [companyOptions, login, loginSelectedCompany]);

  const handleChange = useCallback(
    (company) => {
      if (!company?.id || isSameClientCompanyId(company.id, selectedCompany?.id)) {
        return;
      }

      const nextLogin = applyClientCompanySelection(login, company);
      dispatch(loginResponse(nextLogin));
      setAdmin(nextLogin);

      if (
        location.pathname === ROUTE_PATHS.clientPortalEvents ||
        location.pathname.startsWith(`${ROUTE_PATHS.clientPortalEvents}/`)
      ) {
        history.replace(ROUTE_PATHS.clientPortalEvents);
        return;
      }

      history.push(ROUTE_PATHS.clientPortalEvents);
    },
    [dispatch, history, location.pathname, login, selectedCompany?.id],
  );

  if (!hasMultiple || !selectedCompany) {
    return null;
  }

  return (
    <div className={clx("client-company-switcher", className)}>
      <UISelect
        id="client-company-switcher"
        options={companyOptions}
        value={selectedCompany}
        onChange={handleChange}
        disableClearable
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option?.name || ""
        }
        isOptionEqualToValue={(option, value) =>
          isSameClientCompanyId(option?.id, value?.id)
        }
        noOptionsText={shared.noOptions}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            {option?.name || ""}
          </Box>
        )}
        componentsProps={{
          listbox: {
            "aria-label": copy.switchCompanyMenuAria,
          },
        }}
        textFieldProps={{
          InputProps: {
            readOnly: true,
          },
          inputProps: {
            "aria-label": copy.switchCompanyTriggerAria,
          },
        }}
      />
    </div>
  );
};

ClientCompanySwitcher.propTypes = {
  className: PropTypes.string,
};

ClientCompanySwitcher.defaultProps = {
  className: undefined,
};

export default ClientCompanySwitcher;
